import * as React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Stack,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    CircularProgress,
    Alert,
    Link,
    Collapse,
} from '@mui/material';
import { API_BASE, CARD_SX } from './dashboardShared';
import { COLORS } from '../theme/colors';

const COLUMNS = [
    { key: 'createdAt', label: 'Date' },
    { key: 'name', label: 'Name' },
    { key: 'company', label: 'Company' },
    { key: 'email', label: 'Email' },
];

function formatDate(iso) {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function Messages({ token, onLogout }) {
    const [submissions, setSubmissions] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [search, setSearch] = React.useState('');
    const [sortKey, setSortKey] = React.useState('createdAt');
    const [sortDir, setSortDir] = React.useState('desc');
    const [expandedId, setExpandedId] = React.useState(null);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API_BASE}/api/submissions`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (res.status === 401) {
                    onLogout();
                    return;
                }
                const data = await res.json();
                if (cancelled) return;
                if (!res.ok) {
                    setError(data.message || 'Failed to load messages');
                    return;
                }
                setSubmissions(data);
            } catch (err) {
                if (!cancelled) setError('Could not reach the server. Please try again.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [token, onLogout]);

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
    };

    const handleToggleRead = async (submission) => {
        const nextRead = !submission.read;
        setSubmissions((prev) => prev.map((s) => (s.id === submission.id ? { ...s, read: nextRead } : s)));
        try {
            await fetch(`${API_BASE}/api/submissions/${submission.id}/read`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ read: nextRead }),
            });
        } catch (err) {
            // Best-effort — an occasional failed read-sync isn't worth surfacing an error for.
        }
    };

    const filtered = submissions.filter((s) => {
        const term = search.toLowerCase();
        if (!term) return true;
        return (
            s.name?.toLowerCase().includes(term) ||
            s.email?.toLowerCase().includes(term) ||
            s.message?.toLowerCase().includes(term)
        );
    });

    const sorted = [...filtered].sort((a, b) => {
        const av = a[sortKey] || '';
        const bv = b[sortKey] || '';
        const cmp = av < bv ? -1 : av > bv ? 1 : 0;
        return sortDir === 'asc' ? cmp : -cmp;
    });

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Card sx={CARD_SX}>
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
                    <Typography variant="h6" sx={{ color: 'white' }}>
                        Contact form messages ({submissions.length})
                    </Typography>
                    <TextField
                        size="small"
                        placeholder="Search name, email, message…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{
                            minWidth: 240,
                            '& .MuiOutlinedInput-root': { bgcolor: 'rgba(0,0,0,0.4)', color: 'white' },
                        }}
                    />
                </Stack>

                {sorted.length === 0 ? (
                    <Typography sx={{ color: 'grey.400' }}>No messages yet.</Typography>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {COLUMNS.map((col) => (
                                    <TableCell key={col.key} sx={{ color: 'grey.400' }}>
                                        <TableSortLabel
                                            active={sortKey === col.key}
                                            direction={sortKey === col.key ? sortDir : 'asc'}
                                            onClick={() => handleSort(col.key)}
                                            sx={{
                                                color: 'inherit',
                                                '&.Mui-active': { color: 'white' },
                                                '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                                            }}
                                        >
                                            {col.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sorted.map((s) => (
                                <React.Fragment key={s.id}>
                                    <TableRow
                                        hover
                                        onClick={() => {
                                            setExpandedId(expandedId === s.id ? null : s.id);
                                            if (!s.read) handleToggleRead(s);
                                        }}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell sx={{ color: 'white', fontWeight: s.read ? 400 : 700 }}>
                                            {formatDate(s.createdAt)}
                                        </TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: s.read ? 400 : 700 }}>
                                            {s.name}
                                        </TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: s.read ? 400 : 700 }}>
                                            {s.company || '—'}
                                        </TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: s.read ? 400 : 700 }}>
                                            {s.email}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ p: 0, border: 0 }}>
                                            <Collapse in={expandedId === s.id} unmountOnExit>
                                                <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.03)' }}>
                                                    <Typography sx={{ color: 'grey.400', mb: 1 }}>
                                                        Phone: {s.phone || '—'}
                                                    </Typography>
                                                    <Typography sx={{ color: 'white', whiteSpace: 'pre-wrap', mb: 1 }}>
                                                        {s.message}
                                                    </Typography>
                                                    <Link href={`mailto:${s.email}`} sx={{ color: COLORS.link }}>
                                                        Reply by email
                                                    </Link>
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
}
